import { useState, useCallback, useRef } from "react"

/**
 * Hook to manage a queue of modals that are displayed sequentially
 * Only one modal can be open at a time
 * Modals are sorted by partidaIndex to maintain the correct order
 */
export const useSequentialModals = () => {
  const [queue, setQueue] = useState([])
  const [currentModalData, setCurrentModalData] = useState(null)
  const currentModalRef = useRef(null)
  const isProcessingRef = useRef(false)

  /**
   * Sorts the queue by partidaIndex
   */
  const sortQueue = useCallback(queueToSort => {
    return [...queueToSort].sort((a, b) => {
      const indexA = a.modalData?.partidaIndex ?? Infinity
      const indexB = b.modalData?.partidaIndex ?? Infinity
      return indexA - indexB
    })
  }, [])

  /**
   * Adds a modal to the queue
   * @param {Object} modalData - Modal data to display (must include partidaIndex)
   * @param {Function} onResolve - Callback when the modal is resolved
   * @param {Function} onOpen - Callback to open the modal when it's its turn
   */
  const enqueue = useCallback((modalData, onResolve, onOpen) => {
    setQueue(prev => {
      const newQueue = [...prev, { modalData, onResolve, onOpen }]
      return newQueue
    })
  }, [])

  /**
   * Processes the next modal in the queue
   * Sorts the queue by partidaIndex before processing
   */
  const processNext = useCallback(() => {
    if (isProcessingRef.current || currentModalRef.current) {
      return null
    }

    setQueue(prev => {
      if (prev.length === 0) {
        return prev
      }

      const sortedQueue = sortQueue(prev)
      const [next, ...rest] = sortedQueue

      isProcessingRef.current = true
      currentModalRef.current = next

      if (next.onOpen) {
        next.onOpen()
      }

      setCurrentModalData(next.modalData)

      return rest
    })

    return currentModalRef.current
  }, [sortQueue])

  /**
   * Marks the current modal as resolved and processes the next one
   * @param {*} result - Result of the resolved modal
   */
  const resolveCurrent = useCallback(
    result => {
      if (currentModalRef.current) {
        const { onResolve } = currentModalRef.current
        if (onResolve) {
          onResolve(result)
        }
        currentModalRef.current = null
        setCurrentModalData(null)
        isProcessingRef.current = false
      }

      setTimeout(() => {
        setQueue(prev => {
          if (prev.length > 0) {
            const sortedQueue = sortQueue(prev)
            const [next, ...rest] = sortedQueue
            isProcessingRef.current = true
            currentModalRef.current = next

            if (next.onOpen) {
              next.onOpen()
            }

            setCurrentModalData(next.modalData)
            return rest
          } else {
            isProcessingRef.current = false
          }
          return prev
        })
      }, 100)
    },
    [sortQueue],
  )

  /**
   * Gets the current modal that should be displayed
   */
  const getCurrentModal = useCallback(() => {
    return currentModalData
  }, [currentModalData])

  /**
   * Clears the queue
   */
  const clearQueue = useCallback(() => {
    setQueue([])
    currentModalRef.current = null
    setCurrentModalData(null)
    isProcessingRef.current = false
  }, [])

  /**
   * Checks if there's a modal being processed
   */
  const hasCurrentModal = useCallback(() => {
    return currentModalRef.current !== null
  }, [])

  return {
    enqueue,
    processNext,
    resolveCurrent,
    getCurrentModal,
    clearQueue,
    hasCurrentModal,
    currentModalData,
    queueLength: queue.length,
  }
}
